import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertInternalAuditMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC input type for insertInternalAudit. BusinessArea is free-text (the
 * data-layer resolves it to a BusinessAreaId).
 */
type InsertInternalAuditTRPCInput = {
  Title: string;
  Description?: string | null;
  BusinessArea?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure so
 * consumers don't branch on which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertInternalAuditMutation => {
  return {
    insertInternalAudit: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertInternalAuditTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditEntity.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.internalAuditEntity.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.internalAuditEntity.internalAuditById.queryKey(),
        });
        // A new free-text BusinessArea find-or-creates a business_area row, so
        // refresh the business-area list (parity with the Apollo path's
        // evictField(cache, 'business_area')).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.businessArea.businessAreas.queryKey(),
        });
      },
    }),
  });

  return {
    insertInternalAudit: async (
      variables: InsertInternalAuditTRPCInput
    ): Promise<InsertInternalAuditMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
